export const chat = {
    // Tier retention strip (RetentionBanner) — shown when the viewer's
    // plan hides part of this channel's history.
    retentionBanner:
        "Messages older than {days} days are hidden on your plan. Upgrade to see the full history.",
    composer: {
        placeholder: "Type a message…",
        sendTooltip: "Send",
        attachTooltip: "Attach file",
        emojiTooltip: "Emoji",
        mentionTooltip: "Mention someone",
    },
    todoPane: {
        title: "To-do",
        addPlaceholder: "Add a to-do item",
        complete: "Complete",
        filter: "Filter",
        empty: "No to-do items",
        myTodos: "My Todos",
        completedOfTotal: "{completed} of {total} items completed",
        newTodo: "New Todo",
        startToday: "Start today",
        createTodayTooltip: "Create today's to-do list",
        completedTodayFilter: "Completed today ({count})",
        tagFilterLabel: "Filter by tag",
        tagFilterNone: "No tag",
        tagFilterClear: "Clear",
        todaySuffix: " (Today)",
        startTomorrow: "Start tomorrow",
        createTomorrowTooltip: "Create tomorrow's to-do list",
        tomorrowSuffix: " (Tomorrow)",
        moveToTomorrow: "Move to tomorrow",
        untitled: "Untitled to-do",
        addItemPlaceholder: "+ Add item",
        // Accessible name for the @/# picker that opens over a title field.
        mentions: {
            ariaLabel: "Mention suggestions",
        },
        addSubitem: "Add subitem",
        addSubitemPlaceholder: "+ Add subitem",
        completedTooltip: "Completed {time}",
        collapseNotes: "Collapse notes",
        expandNotes: "Expand notes",
        moreOptions: "More options",
        tag: "Tag",
        general: "General",
        newTagPlaceholder: "New tag",
        // Manage-tags modal (MIP-759). Tags are personal to each user.
        tags: {
            manageTitle: "Manage tags",
            manageTooltip: "Manage tags",
            privateHint: "Your tags are private to you. Click a name to rename it.",
            emptyList: "No tags yet. Add one below.",
            createButton: "Add",
            deleteConfirm: "Delete",
            close: "Close",
            invalidName: "Enter a tag name (up to 64 characters) that you do not already use.",
            createFailed: "Could not create that tag.",
            updateFailed: "Could not save that change. The name may already be in use.",
        },
        delete: "Delete",
        completedTodayEmptyTitle: "Nothing finished yet today",
        completedTodayEmptySubtitle: "Items you complete today will show up here.",
        incompleteEmptySubtitle: "No incomplete items in your visible groups.",
        emptySubtitle: "Create today’s group to get started.",
        sourceDmThread: "From DM thread",
        sourceDm: "From DM",
        sourceGmThread: "From GM thread",
        sourceGm: "From GM",
        sourceTaskComment: "From task comment",
        sourceMdmThread: "From multi-user DM thread",
        sourceMdm: "From multi-user DM",
        existingTodayTooltip: "You already have a todo for today.",
        allFilter: "All ({count})",
        incompleteFilter: "Incomplete ({count})",
        allCaughtUp: "All caught up!",
        noTodosYet: "No todos yet",
        allCompletedSubtitle: "You've completed all your tasks",
        createFirstSubtitle: "Create your first todo to get started",
        save: "Save",
        saved: "Saved",
        copyLink: "Copy link",
        linkCopied: "Link copied",
        // The row's "Remind me…" item. The picker itself is the shared
        // `chat.remindMe` dialog, so only the labels around it live here:
        // `menu` when nothing is set, `setFor` (with the time) when one is
        // pending, and `description` replacing the message-flavoured
        // subtitle inside the dialog.
        remindMe: {
            menu: "Remind me…",
            setFor: "Reminder: {time}",
            description:
                "You'll get a notification and an inbox activity. Ticking this off cancels it.",
        },
        createTask: {
            menu: "Create task",
            heading: "Create a task from this to-do",
            description:
                "Copies the title (and any notes) into a new task. The to-do itself is left as it is.",
            titleLabel: "Task title",
            titlePlaceholder: "What needs doing?",
            projectLabel: "Project",
            milestoneLabel: "Milestone (optional)",
            loadingProjects: "Loading projects…",
            noProjects: "You're not in any project yet. Join or create one to file a task.",
            cancel: "Cancel",
            submit: "Create task",
            failed: "Couldn't create the task. Please try again.",
        },
        schedule: {
            tooltip: "Scheduled todos",
            heading: "Scheduled todos",
            description:
                "Recurring todos are added to today's list automatically — no need to start today by hand.",
            titleLabel: "Todo title",
            titlePlaceholder: "e.g. Daily standup",
            categoryLabel: "Tag (optional)",
            noCategory: "No tag",
            add: "Add schedule",
            adding: "Adding…",
            listHeading: "Your schedules",
            empty: "No scheduled todos yet. Add one above.",
            active: "Active",
            paused: "Paused",
            pause: "Pause",
            resume: "Resume",
            delete: "Delete",
            deleteConfirm: "Delete this schedule? Todos already added stay.",
            needsRepeat: "Pick how often this repeats.",
            failed: "Couldn't save the schedule. Please try again.",
            // Human-readable summary of a rule, e.g. "Daily", "Weekly on
            // Mon, Wed", "Every 2 months".
            summaryDaily: "Daily",
            summaryEveryNDays: "Every {n} days",
            summaryWeekly: "Weekly",
            summaryWeeklyOn: "Weekly on {days}",
            summaryEveryNWeeks: "Every {n} weeks",
            summaryEveryNWeeksOn: "Every {n} weeks on {days}",
            summaryMonthly: "Monthly",
            summaryEveryNMonths: "Every {n} months",
            summaryYearly: "Yearly",
            summaryEveryNYears: "Every {n} years",
        },
    },
    thread: {
        replyPlaceholder: "Reply in thread…",
        newThread: "Start a thread",
    },
    attachments: {
        uploading: "Uploading attachments…",
        uploadFailed: "Upload failed. Try again.",
    },
    sidebar: {
        searchAriaLabel: "Search",
        searchPlaceholder: "Search people & groups...",
        navDM: "Direct Messages",
        navDMShort: "DMs",
        navGM: "Group Messages",
        navGMShort: "Groups",
        navPM: "Project Updates",
        navPMShort: "Projects",
        navFlagged: "Flagged Messages",
        navFlaggedShort: "Flagged",
        navActivity: "Recent Activities (last 30 days)",
        navActivityShort: "Activity",
        currentSectionFallback: "Messages",
        // Placeholder labels for a list row whose message is media-only
        // and so has no preview text of its own (a GIF, an image, a
        // table). Rendered italic + muted, not as message text.
        previewGif: "GIF",
        previewImage: "Image",
        previewVideo: "Video",
        previewAudio: "Audio",
        previewFile: "File",
        previewTable: "Table",
        unreadCount: "{count, plural, one {# unread message} other {# unread messages}}",
        allCaughtUp: "All caught up",
        unreadFilter: "Unread",
        // Short on purpose — it sits inline next to the chat name, and
        // "people from another organization are in here" has to be legible
        // before the row is clicked, not after.
        externalBadge: "External",
        externalBadgeHint: "People from another team are in this chat",
        sharedByTeamHint: "{team} shared this chat with your team",
        newGroupMessageMenu: "New Group Message",
        newDmWithFriendsMenu: "New DM with friends",
        markFilteredActivitiesReadMenu: "Mark filtered as read",
        dividerPinned: "Pinned Messages",
        dividerGroupMessages: "Group Messages",
        dividerDirectMessages: "Direct Messages",
        dividerProjectUpdates: "Project Updates",
        activityFilterAll: "All",
        activityFilterMentions: "Mentions",
        activityFilterThreads: "Threads",
        activityFilterReactions: "Reactions",
        activityFilterTasks: "Tasks",
        // "Custom" menu trigger sits at the end of the single-select
        // chip row in `ActivityDivider`. Clicking it opens a Joy `Menu`
        // with one item per chip predicate; selections AND-compose
        // inside the menu. Primary single-select and Custom multi-
        // select are mutually exclusive — see `handlePrimaryClick` /
        // `toggleChip` in ActivityDivider.
        // Keys sorted alphabetically per `sort-keys`.
        chipDM: "DM",
        chipMDM: "MDM",
        chipFilterByGroupEmpty: "No mention groups yet",
        chipFilterByGroupLabel: "By group",
        chipFilterByNameEmpty: "No chats yet",
        chipFilterByNameLabel: "By name",
        chipFilterClear: "Clear all filters",
        chipFilterCustomLabel: "Custom",
        chipGM: "GM",
        chipMention: "Mention",
        chipNoteChat: "Chat note",
        chipNoteMy: "My note",
        chipNoteTask: "Task note",
        chipPM: "PM",
        chipProject: "Project",
        chipReaction: "Reaction",
        chipReply: "Reply",
        chipTask: "Task",
        chipTaskComment: "Task comment",
        chipThread: "Thread",
        emptyDMTitle: "No direct messages",
        emptyDMSubtitle: "Start a conversation with someone",
        emptyGMTitle: "No group messages",
        emptyGMSubtitle: "Create or join a group to get started",
        // Personal-tag filter row above the GM list (`GMTagFilterRow`).
        // "GM filtered" empty state = a tag filter with zero matches —
        // must not claim "No group messages".
        emptyGMFilteredTitle: "No chats match these tags",
        emptyGMFilteredSubtitle: "Try different tags or clear the tag filter",
        gmTagFilterClear: "Clear tag filter",
        gmTagFilterMenuLabel: "Tags",
        gmTagManageMenu: "Manage tags…",
        gmTagPinDefault: "Always show this chip",
        gmTagUnpinDefault: "Stop always showing this chip",
        emptyPMTitle: "No project updates",
        emptyPMSubtitle: "Project conversations will appear here",
        emptyActivityTitle: "No activities yet",
        emptyActivitySubtitle: "Mentions and replies will show up here",
        emptyFlaggedTitle: "No flagged messages",
        emptyFlaggedSubtitle: "Flag important messages to find them later",
        emptyPastFlaggedTitle: "No completed flags",
        emptyPastFlaggedSubtitle: "Flags you mark done show up here",
        viewPastFlaggedMenu: "View completed flags",
        viewActiveFlaggedMenu: "View active flags",
        backToActiveFlaggedButton: "Back to active flags",
    },
    listItem: {
        backToChat: "Back to Chat",
        openTodo: "Open To-Do",
        pinChat: "Pin Chat",
        unpinChat: "Unpin Chat",
        unflag: "Unflag",
        completeFlag: "Mark done",
        reopenFlag: "Reopen",
        chatRemoved: "Chat removed",
        messageDeleted: "Message deleted",
        unknown: "Unknown",
        addMembersMenu: "Add members",
        manageTagsMenu: "Tags",
        openInSplitView: "Open in split view",
        threadChip: "Thread",
    },
    // Personal GM tags (private per-user labels) — the assign +
    // manage modals opened from the GM row ⋮ menu / tag filter row.
    gmTags: {
        assignTitle: "Tags for {name}",
        cancelButton: "Cancel",
        createButton: "Add",
        createFailed: "Couldn't create the tag. Try again.",
        createPlaceholder: "New tag name",
        deleteConfirm: "Delete?",
        emptyAssign: "No tags yet — create your first below.",
        emptyList: "No tags yet — create your first below.",
        invalidName: "Enter a unique name (30 characters max).",
        manageTitle: "Manage tags",
        maxPerChatReached: "A chat can carry at most 20 tags.",
        privateHint: "Tags are private — only you can see them.",
        saveButton: "Save",
        saveFailed: "Couldn't save tags. Try again.",
    },
    activity: {
        aggregatedEarlier: "+{count} earlier on this topic",
        aggregatedNew: "+{count} more new since you looked",
        chipTaskComment: "Task",
        chipReply: "Reply",
        chipReaction: "Reaction",
        chipMention: "Mention",
        chipThread: "Thread",
        // BlockNote inline comment left on a task body / note — tags both a
        // mention-in-comment and the general participant fan-out row.
        chipComment: "Comment",
        chipTaskBody: "Task",
        chipPersonalNote: "Note",
        chipTaskNote: "Task note",
        chipChatNote: "Chat note",
        copied: "Copied!",
        copyTooltip: "Click to copy",
    },
    headers: {
        markAllReadAria: "Mark all in this chat as read",
        createNewTaskTooltip: "Create a new task",
        backToChat: "Back to Chat",
        backToDM: "Back to DM",
        todoTooltip: "To-Do",
        swapChats: "Swap chats",
        addMembers: "Add members",
        close: "Close",
        thread: "Thread",
        openTask: "Open Task",
        newTaskTooltip: "New task linked to this thread",
        newTaskAria: "Create a new task linked to this thread",
        // Short labels for the thread header's ⋮ (More) menu items.
        threadTaskMenuItem: "Thread task",
        threadNoteMenuItem: "Thread note",
        copyThreadLink: "Copy thread link",
        // Breadcrumb badge fallbacks when the task has no displayId.
        threadTaskBadge: "Task",
        threadMilestoneBadge: "Milestone",
        openNoteTooltip: "Open Note linked to this thread",
        online: "Online",
        offline: "Offline",
        quickMeetTooltip: "Start a Quick Meet — copy a link and optionally share it",
        quickMeetEventTitle: "Quick Meet",
        quickMeetGenerating: "Generating Meet link…",
        quickMeetFailed: "Couldn't generate a Meet link. Try again.",
        quickMeetNotConnected: "Connect Google Calendar in Integrations to use Quick Meet.",
        quickMeetScopeMissing:
            "Calendar access hasn't been granted yet — grant it to use Quick Meet.",
        quickMeetGrant: "Grant access",
        quickMeetReauth: "Google Calendar connection expired — reconnect to use Quick Meet.",
        quickMeetReconnect: "Reconnect",
    },
    threadTabs: {
        comments: "Comments",
        activities: "Activities",
    },
    bubble: {
        clickToOpenTask: "Click to open task",
        clickToOpenThread: "{modKey}+Click to open thread",
        updated: "Updated",
    },
    messageActions: {
        openTask: "Open task",
        addCommentToTask: "Add comment to task",
        replyInThread: "Reply in thread",
        flagForLater: "Flag for later",
        removeFlag: "Remove flag",
        remindMe: "Remind me…",
        // Replaces the label above once a reminder exists, so the menu
        // states the promise instead of hiding it behind another click.
        reminderSetFor: "Reminder: {time}",
        copyMessageLink: "Copy message link",
        editMessage: "Edit message",
        deleteMessage: "Delete message",
        // Per-bubble wrap toggles. Pair of labels per action so the
        // menu item flips depending on the current toggle state.
        unwrapAll: "Unwrap content",
        wrapAll: "Wrap content",
        unwrapCode: "Unwrap code blocks",
        wrapCode: "Wrap code blocks",
    },
    // The "remind me about this message" picker (ModalRemindMe). Preset
    // keys match `ReminderPresetId` in `utils/reminderPresets.ts`; the
    // times beside them are computed, so these labels say only the shape
    // of the delay.
    remindMe: {
        title: "Remind me about this",
        description: "You'll get a notification and an inbox activity. The message stays flagged.",
        currentlySetFor: "Reminder set for {time}. Pick another time to change it.",
        presets: {
            in20m: "In 20 minutes",
            in1h: "In 1 hour",
            in3h: "In 3 hours",
            tomorrow: "Tomorrow morning",
            nextWeek: "Next week",
        },
        customLabel: "Or pick a date and time",
        customInvalid: "Choose a time in the future, within the next year.",
        setButton: "Set",
        remove: "Remove reminder",
        failed: "Couldn't update the reminder. Please try again.",
    },
    modals: {
        deleteMessage: {
            title: "Delete Message?",
            description: "This action cannot be undone",
            cancel: "Cancel",
            delete: "Delete",
            errorDeleteFromThread: "Failed to delete message from thread",
            errorDeleteFromChat: "Failed to delete message from chat",
        },
        shareMeetLink: {
            title: "Share Meet Link?",
            description: "The link is already copied to your clipboard.",
            cancel: "Cancel",
            share: "Share in chat",
        },
        createGM: {
            title: "Create New Group Message",
            groupNamePlaceholder: "Enter group name...",
            privateGroup: "Private Group",
            publicGroup: "Public Group",
            searchPlaceholder: "Search team members...",
            noMembersMatchingSearch: "No members found matching your search.",
            noMembersAvailable: "No team members available.",
            membersSelected:
                "{count} {count, plural, one {member} other {members}} selected (optional)",
            cancel: "Cancel",
            createGroup: "Create Group",
            externalChat: "Include another organization",
            // Says who chooses whom, because that is the part people get
            // wrong: you invite the TEAM, they pick their own people.
            externalChatExplainer:
                "Pick connected teams to invite. Each team approves once, then their owner or editor chooses which of their members join. External chats are always private.",
        },
        createMDM: {
            title: "New Multi-user DM",
            description: "Start a conversation with multiple people without creating a group.",
            searchPlaceholder: "Search team members...",
            noMembersMatchingSearch: "No members found matching your search.",
            noMembersAvailable: "No team members available.",
            errorCreate: "Failed to create multi-user DM. Please try again.",
            errorMinimumMembers:
                "Select at least 2 other members for a multi-user DM (3 people total).",
            membersSelected: "{count} {count, plural, one {member} other {members}} selected",
            minRequiredSuffix: " (2 others required; 3 people total)",
            cancel: "Cancel",
            startConversation: "Start Conversation",
        },
        addMembers: {
            title: "Talk with more members",
            currentConversationWith: "Current conversation with:",
            currentMembers: "Current members:",
            searchPlaceholder: "Search team members...",
            noMembersMatchingSearch: "No members found matching your search.",
            noMembersAvailable: "No team members available to add.",
            errorAdd: "Failed to add members. Please try again.",
            errorMinimumMembers: "Please select at least 1 member to add.",
            membersSelected: "{count} {count, plural, one {member} other {members}} selected",
            cancel: "Cancel",
            addMembers: "Add Members",
        },
        gmProfile: {
            editProfileImageTooltip: "Edit Profile Image",
            searchMembersPlaceholder: "Search members...",
            yes: "Yes",
            no: "No",
            uploadImageError: "Failed to upload user profile image.",
            owner: "Owner",
            membersCount: "Members ({filtered}/{total})",
            noMembersFound: 'No members found matching "{query}"',
            isPrivate: "Is Private",
            createdDate: "Created Date",
            na: "N/A",
            // Names the KIND of chat, not just the fact of a share: this
            // sits beside a title that already names the group, and
            // "External" alone next to a group name reads as an attribute
            // of the group's owner rather than of who is in it.
            externalBadge: "External GM chat",
            groupName: "Group name",
        },
        joinGM: {
            sendInboxError: "Failed to send an inbox message.",
            socketNotFound: "Socket not found.",
            privateGroupLabel: "Private Group",
            requestToJoin: "Request to Join",
            cancel: "Cancel",
            sendRequest: "Send Request",
            joinRequestPrefix: "You sent a request to join ",
            joinRequestSuffix: ".",
        },
    },
    emptyPane: {
        title: "No conversation selected",
        subtitle: "Choose a chat from the sidebar to start messaging",
        hint: "Select from sidebar",
    },
    feedback: {
        todoAddedFromMessage: "To-do added from the message.",
    },
    errors: {
        groupNameRequired: "Group name is required.",
        createGroupFailed: "Failed to create group. Please try again.",
        mdmMinimumMembers: "A multi-user DM requires at least 3 distinct members.",
        createMdmFailed: "Failed to create multi-user DM. Please try again.",
        addMembersFailed: "Failed to add members. Please try again.",
        renameGroupFailed: "Failed to rename the group.",
        deleteMessageIdUnavailable: "Could not delete: message ID unavailable.",
        deleteMessageFailed: "Failed to delete message.",
    },
    system: {
        hasJoined: "Has joined",
        hasCreatedGroup: "Has created this group",
        startedConversation: "Started this conversation",
        needToAdd: "Need to add",
    },
    reactions: {
        andMore: " and more",
        reacted: " reacted",
        reactedLabel: "reacted",
    },
    // v3 channel surface (`src/features/channel/**`). Kept in its own
    // section rather than folded into `composer` / `messageActions` /
    // `listItem` above: those belong to the legacy chat UI, and the two
    // implementations run side by side until the v3 cutover. Merging
    // them would make a later legacy deletion take v3 strings with it.
    channel: {
        list: {
            pinned: "Pinned",
            pinChannel: "Pin channel",
            unpinChannel: "Unpin channel",
            searchPlaceholder: "Search channels…",
            loading: "Loading…",
            empty: "No channels yet.",
            noResults: "No channels match “{query}”.",
        },
        pane: {
            backToChannels: "Back to channels",
            mentionsYouBadge: "This message mentions you",
            flaggedIndicator: "You flagged this message",
            noMessages: "No messages yet.",
            dismissError: "click to dismiss",
            deleteConfirm: "Delete this message?",
            deleted: "(deleted)",
            edited: "(edited)",
        },
        // Per-message hover toolbar (MessageRowHoverToolbar). Labels
        // are terser than the legacy `messageActions` ones above
        // because these sit on icon-only buttons, not menu rows.
        messageActions: {
            flagMessage: "Flag message",
            unflagMessage: "Unflag message",
            addReaction: "Add reaction",
            replyInThread: "Reply in thread",
            copyLink: "Copy link to message",
            edit: "Edit",
            delete: "Delete",
            clipboardUnavailable: "Clipboard API unavailable.",
            copyFailed: "Copy failed: {error}",
        },
        thread: {
            flag: "Flag",
            unflag: "Unflag",
            title: "Thread",
            close: "Close",
            loading: "Loading thread…",
            deleted: "(deleted)",
            edited: "(edited)",
            noReplies: "No replies yet.",
            rootMissing: "Thread root not loaded.",
            dismissError: "click to dismiss",
            replyPlaceholder: "Reply… (type @ to mention)",
            uploading: "Uploading…",
            reply: "Reply",
        },
        composer: {
            attachFiles: "Attach file(s)",
        },
        attachments: {
            remove: "Remove",
            sizeExceeded: "File exceeds the {limit} limit for your plan.",
        },
        // Inline chips rendered inside a message body (MessageBody).
        body: {
            mentionsYou: "Mentions you",
            mentionsUser: "Mentions {name}",
            groupMembers: "{count, plural, one {# member} other {# members}}",
        },
    },
} as const;
